import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import PageShell from "@/components/PageShell";
import { fmtDate, EmptyState, Skeleton } from "@/components/Shared";
import {
  EnvelopeSimple, CheckCircle, XCircle, Ghost, MagnifyingGlass,
  ArrowsClockwise, PaperPlaneTilt, Eye, CursorClick, User, ChatCircle,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import DatePickerCompact from "@/components/DatePickerCompact";
import { toast } from "sonner";

const KIND_LABEL = {
  invoice_created: "Factura creada",
  reminder_before: "Recordatorio previo",
  due_today: "Vence hoy",
  overdue: "Factura vencida",
  disconnection_notice: "Aviso de desconexión",
  manual_resend: "Envío manual",
  password_reset: "Restablecer contraseña",
};

const STATUS_CHIPS = [
  { key: "all", label: "Todos" },
  { key: "sent", label: "Entregados" },
  { key: "failed", label: "Fallidos" },
  { key: "mocked", label: "Modo prueba" },
];

function StatusBadge({ item }) {
  if (item.error || !item.sent) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900">
        <XCircle size={11} weight="fill" /> Fallido
      </span>
    );
  }
  if (item.mocked) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-slate-100 text-slate-600 border border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700">
        <Ghost size={11} weight="fill" /> Prueba
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900">
      <CheckCircle size={11} weight="fill" /> Entregado
    </span>
  );
}

function StatCard({ label, value, tone = "slate", "data-testid": testid }) {
  const tones = {
    slate: "text-slate-900 dark:text-white",
    emerald: "text-emerald-700 dark:text-emerald-300",
    rose: "text-rose-700 dark:text-rose-300",
    amber: "text-amber-700 dark:text-amber-300",
  };
  return (
    <div className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</div>
      <div className={`mt-1 text-2xl font-semibold font-mono tabular-nums ${tones[tone]}`}>{value}</div>
    </div>
  );
}

export default function EmailLogPage() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [kind, setKind] = useState("");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [skip, setSkip] = useState(0);
  const [selected, setSelected] = useState(null);
  const [detailDoc, setDetailDoc] = useState(null); // full doc with html, loaded on open
  const [detailLoading, setDetailLoading] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [timelineData, setTimelineData] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [resending, setResending] = useState(null); // provider_id or created_at key
  const [selectedIds, setSelectedIds] = useState(() => new Set()); // provider_ids selected for bulk
  const [bulkResending, setBulkResending] = useState(false);
  const PAGE_SIZE = 50;

  const loadStats = async () => {
    try {
      const { data } = await api.get("/emails/log/stats");
      setStats(data);
    } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[emails] stats fetch failed:", e?.message); }
  };

  const load = async (nextSkip = 0) => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip: nextSkip };
      if (status !== "all") params.status = status;
      if (kind) params.kind = kind;
      if (search.trim()) params.search = search.trim();
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const { data } = await api.get("/emails/log", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setSkip(nextSkip);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { load(0); /* eslint-disable-next-line */ }, [status, kind, fromDate, toDate]);

  const kindOptions = useMemo(() => {
    const keys = Object.keys(stats?.by_kind_30d || {});
    return keys.sort();
  }, [stats]);

  const onSearch = (e) => {
    e.preventDefault();
    load(0);
  };

  // Date-range quick selectors
  const todayISO = () => new Date().toISOString().slice(0, 10);
  const daysAgoISO = (d) => {
    const t = new Date(); t.setDate(t.getDate() - d);
    return t.toISOString().slice(0, 10);
  };
  const applyRange = (f, t) => { setFromDate(f || ""); setToDate(t || ""); };
  const rangePresets = [
    { key: "all",   label: "Todo",       f: "",            t: "" },
    { key: "today", label: "Hoy",        f: todayISO(),    t: todayISO() },
    { key: "7d",    label: "7 días",     f: daysAgoISO(7), t: todayISO() },
    { key: "30d",   label: "30 días",    f: daysAgoISO(30), t: todayISO() },
    { key: "90d",   label: "Trimestre",  f: daysAgoISO(90), t: todayISO() },
  ];
  const activePreset = rangePresets.find((p) => p.f === fromDate && p.t === toDate)?.key || "custom";

  // Open detail dialog — fetch full doc (with html) by provider_id
  const openDetail = async (item) => {
    setSelected(item);
    setDetailDoc(null);
    if (!item.provider_id) {
      // No provider_id → list already has subject/meta, just use `item`
      setDetailDoc(item);
      return;
    }
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/emails/log/${encodeURIComponent(item.provider_id)}`);
      setDetailDoc(data);
    } catch {
      setDetailDoc(item);
    } finally {
      setDetailLoading(false);
    }
  };
  const closeDetail = () => { setSelected(null); setDetailDoc(null); };

  // Timeline for a client
  const openTimeline = async (clientId) => {
    if (!clientId) return;
    setTimelineOpen(true);
    setTimelineLoading(true);
    setTimelineData(null);
    try {
      const { data } = await api.get(`/emails/log/by-client/${encodeURIComponent(clientId)}`);
      setTimelineData(data);
    } catch (e) {
      toast.error("No se pudo cargar el historial del cliente");
      setTimelineOpen(false);
    } finally {
      setTimelineLoading(false);
    }
  };

  const resend = async (item, e) => {
    e?.stopPropagation();
    const key = item.provider_id || item.created_at;
    setResending(key);
    try {
      const body = item.provider_id
        ? { provider_id: item.provider_id }
        : { to: item.to, created_at: item.created_at };
      const { data } = await api.post("/emails/log/resend", body);
      toast.success(`Reenviado a ${data.to}${data.mocked ? " (modo prueba)" : ""}`);
      loadStats();
      load(skip);
    } catch (err) {
      const msg = err.response?.data?.detail || "No se pudo reenviar";
      toast.error(typeof msg === "string" ? msg : "No se pudo reenviar");
    } finally {
      setResending(null);
    }
  };

  const canResend = (it) => {
    // Password reset codes are one-time secrets; only invoice-linked emails resend.
    if (it.kind === "password_reset") return false;
    return !!(it.meta && it.meta.invoice_id);
  };

  // ─── Bulk selection helpers ──────────────────────────────────────
  const eligibleOnPage = useMemo(
    () => items.filter((it) => canResend(it) && it.provider_id),
    [items]
  );
  const allEligibleSelected = eligibleOnPage.length > 0 && eligibleOnPage.every((it) => selectedIds.has(it.provider_id));
  const someSelected = selectedIds.size > 0;

  const toggleOne = (pid, e) => {
    e?.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  const toggleAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allEligibleSelected) {
        eligibleOnPage.forEach((it) => next.delete(it.provider_id));
      } else {
        eligibleOnPage.forEach((it) => next.add(it.provider_id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const bulkResend = async () => {
    if (selectedIds.size === 0) return;
    setBulkResending(true);
    try {
      const { data } = await api.post("/emails/log/resend-bulk", {
        provider_ids: Array.from(selectedIds),
      });
      if (data.sent > 0 && data.failed === 0) {
        toast.success(`${data.sent} correos reenviados correctamente`);
      } else if (data.sent > 0 && data.failed > 0) {
        toast.warning(`${data.sent} enviados · ${data.failed} fallaron`);
      } else {
        toast.error(`No se pudo reenviar ninguno (${data.failed} fallos)`);
      }
      clearSelection();
      loadStats();
      load(skip);
    } catch (err) {
      const msg = err.response?.data?.detail || "Error en reenvío masivo";
      toast.error(typeof msg === "string" ? msg : "Error en reenvío masivo");
    } finally {
      setBulkResending(false);
    }
  };

  // Auto-clear selection when filters change (rows may disappear)
  useEffect(() => { clearSelection(); }, [status, kind, skip]);

  return (
    <PageShell
      eyebrow="Comunicación"
      title="Historial de correos"
      subtitle="Todos los emails enviados por la plataforma · último 30 días de auditoría"
      actions={(
        <Button variant="outline" size="sm" onClick={() => { loadStats(); load(skip); }} data-testid="email-log-refresh">
          <ArrowsClockwise size={14} className="mr-1.5" /> Actualizar
        </Button>
      )}
    >
      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <StatCard label="Últimos 30 d" value={stats?.total_30d ?? "—"} data-testid="stat-total-30d" />
        <StatCard label="Entregados" value={stats?.sent_30d ?? "—"} tone="emerald" data-testid="stat-sent-30d" />
        <StatCard label="Fallidos" value={stats?.failed_30d ?? "—"} tone="rose" data-testid="stat-failed-30d" />
        <StatCard label="Tasa entrega" value={stats ? `${stats.delivery_rate_30d}%` : "—"} tone="slate" data-testid="stat-delivery-rate" />
        <StatCard label="Hoy" value={stats?.total_today ?? "—"} tone="amber" data-testid="stat-today" />
      </div>

      {/* Filters row */}
      <div className="flex flex-col gap-3 mb-4">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          {/* Status chips */}
          <div className="flex items-center gap-1 border border-slate-200 dark:border-slate-800 rounded p-0.5 bg-white dark:bg-slate-900">
            {STATUS_CHIPS.map((c) => (
              <button
                key={c.key}
                onClick={() => setStatus(c.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                  status === c.key
                    ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                }`}
                data-testid={`filter-status-${c.key}`}
              >
                {c.label}
              </button>
            ))}
          </div>

          {/* Kind dropdown */}
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="h-8 text-xs border border-slate-200 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100 rounded px-2 bg-white focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
            data-testid="filter-kind"
          >
            <option value="">Todos los tipos</option>
            {kindOptions.map((k) => (
              <option key={k} value={k}>{KIND_LABEL[k] || k}</option>
            ))}
          </select>

          {/* Search */}
          <form onSubmit={onSearch} className="flex-1 md:max-w-md flex items-center gap-2">
            <div className="relative flex-1">
              <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por correo o asunto…"
                className="pl-8 h-8 text-xs"
                data-testid="filter-search"
              />
            </div>
            <Button type="submit" size="sm" variant="outline" data-testid="filter-search-submit">Buscar</Button>
          </form>
        </div>

        {/* Date range row */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Rango</span>
          <div className="flex items-center gap-1 border border-slate-200 dark:border-slate-800 rounded p-0.5 bg-white dark:bg-slate-900">
            {rangePresets.map((p) => (
              <button
                key={p.key}
                onClick={() => applyRange(p.f, p.t)}
                className={`px-2.5 py-1 text-[11px] font-medium rounded transition-colors ${
                  activePreset === p.key
                    ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                }`}
                data-testid={`range-preset-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <DatePickerCompact
              value={fromDate}
              onChange={setFromDate}
              placeholder="Desde"
              maxDate={toDate || undefined}
              className="w-40"
              data-testid="filter-from-date"
            />
            <span className="text-xs text-slate-400">→</span>
            <DatePickerCompact
              value={toDate}
              onChange={setToDate}
              placeholder="Hasta"
              minDate={fromDate || undefined}
              className="w-40"
              data-testid="filter-to-date"
            />
            {(fromDate || toDate) && (
              <button
                onClick={() => applyRange("", "")}
                className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-900 dark:hover:text-white font-semibold px-2"
                data-testid="range-clear"
              >
                Limpiar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Bulk selection toolbar */}
      {someSelected && (
        <div className="mb-3 flex items-center justify-between rounded-md border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-4 py-2.5" data-testid="bulk-toolbar">
          <div className="text-sm text-amber-900 dark:text-amber-200">
            <span className="font-semibold font-mono tabular-nums">{selectedIds.size}</span>
            {" "}correos seleccionados para reenvío
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={clearSelection} data-testid="bulk-clear">Limpiar</Button>
            <Button
              size="sm"
              onClick={bulkResend}
              disabled={bulkResending}
              data-testid="bulk-resend-btn"
            >
              {bulkResending
                ? <ArrowsClockwise size={13} className="animate-spin mr-1.5" />
                : <PaperPlaneTilt size={13} weight="fill" className="mr-1.5" />}
              Reenviar {selectedIds.size} correo{selectedIds.size !== 1 ? "s" : ""}
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40">
              <th className="w-10 px-3 py-2.5">
                <input
                  type="checkbox"
                  aria-label="Seleccionar todos los reenviables de la página"
                  checked={allEligibleSelected}
                  onChange={toggleAllOnPage}
                  disabled={eligibleOnPage.length === 0}
                  className="h-3.5 w-3.5 accent-slate-900 cursor-pointer disabled:cursor-not-allowed disabled:opacity-30"
                  data-testid="select-all-checkbox"
                />
              </th>
              <th className="px-4 py-2.5 text-left text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 w-28">Fecha</th>
              <th className="px-4 py-2.5 text-left text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Destinatario</th>
              <th className="px-4 py-2.5 text-left text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Asunto</th>
              <th className="px-4 py-2.5 text-left text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 w-36">Tipo</th>
              <th className="px-4 py-2.5 text-left text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 w-28">Estado</th>
              <th className="px-4 py-2.5 text-left text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 w-24">Tracking</th>
              <th className="px-4 py-2.5 text-left text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 w-20">Proveedor</th>
              <th className="px-4 py-2.5 text-right text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 w-28">Acción</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="p-8"><Skeleton /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={9} className="p-0">
                <EmptyState icon={EnvelopeSimple} title="Sin correos" subtitle="No hay envíos que coincidan con el filtro actual" />
              </td></tr>
            ) : items.map((it, i) => {
              const canR = canResend(it) && !!it.provider_id;
              const isSel = canR && selectedIds.has(it.provider_id);
              return (
              <tr
                key={it.provider_id || `${it.created_at}-${i}`}
                onClick={() => openDetail(it)}
                className={`border-b border-slate-100 dark:border-slate-800 last:border-0 cursor-pointer transition-colors ${isSel ? "bg-amber-50 dark:bg-amber-950/20 hover:bg-amber-100 dark:hover:bg-amber-950/40" : "hover:bg-slate-50 dark:hover:bg-slate-800/40"}`}
                data-testid={`email-row-${i}`}
              >
                <td className="w-10 px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    aria-label="Seleccionar correo"
                    checked={isSel}
                    disabled={!canR}
                    onChange={(e) => canR && toggleOne(it.provider_id, e)}
                    className="h-3.5 w-3.5 accent-slate-900 cursor-pointer disabled:cursor-not-allowed disabled:opacity-30"
                    data-testid={`select-row-${i}`}
                  />
                </td>
                <td className="px-4 py-2.5 text-xs text-slate-500 font-mono tabular-nums whitespace-nowrap">{fmtDate(it.created_at)}</td>
                <td className="px-4 py-2.5 text-xs text-slate-800 dark:text-slate-200 font-mono truncate max-w-[220px]">{it.to}</td>
                <td className="px-4 py-2.5 text-xs text-slate-700 dark:text-slate-300 truncate max-w-[420px]">{it.subject}</td>
                <td className="px-4 py-2.5 text-[11px] text-slate-500">{KIND_LABEL[it.kind] || it.kind || "—"}</td>
                <td className="px-4 py-2.5"><StatusBadge item={it} /></td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-1.5">
                    {it.opened_at ? (
                      <span title={`Abierto · ${new Date(it.opened_at).toLocaleString()}`} className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                        <Eye size={11} weight="fill" /> Abierto
                      </span>
                    ) : it.bounced_at ? (
                      <span title={`Rebotó · ${new Date(it.bounced_at).toLocaleString()}`} className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wider text-rose-700 dark:text-rose-400">
                        <XCircle size={11} weight="fill" /> Rebote
                      </span>
                    ) : it.delivered_at ? (
                      <span title={`Entregado · ${new Date(it.delivered_at).toLocaleString()}`} className="inline-flex items-center gap-0.5 text-[10px] text-slate-500 dark:text-slate-400">
                        <CheckCircle size={11} /> Entregado
                      </span>
                    ) : (
                      <span className="text-[10px] text-slate-300 dark:text-slate-600">—</span>
                    )}
                    {it.clicked_at && (
                      <span title={`Hizo clic · ${new Date(it.clicked_at).toLocaleString()}`} className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-400">
                        <CursorClick size={11} weight="fill" />
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-400 font-semibold font-mono">{it.provider || "—"}</td>
                <td className="px-4 py-2.5 text-right">
                  {canResend(it) ? (
                    <Button
                      size="sm"
                      variant={it.error ? "default" : "outline"}
                      onClick={(e) => resend(it, e)}
                      disabled={resending === (it.provider_id || it.created_at)}
                      className="h-7 text-[10px] uppercase tracking-wider"
                      data-testid={`resend-btn-${i}`}
                    >
                      {resending === (it.provider_id || it.created_at)
                        ? <ArrowsClockwise size={11} className="animate-spin mr-1" />
                        : <PaperPlaneTilt size={11} weight="fill" className="mr-1" />}
                      Reenviar
                    </Button>
                  ) : (
                    <span className="text-[10px] text-slate-300 dark:text-slate-600">—</span>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40 text-xs">
            <span className="text-slate-500 font-mono">
              {skip + 1}–{Math.min(skip + items.length, total)} de {total}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={skip === 0} onClick={() => load(Math.max(0, skip - PAGE_SIZE))} data-testid="page-prev">
                ← Anterior
              </Button>
              <Button variant="outline" size="sm" disabled={skip + PAGE_SIZE >= total} onClick={() => load(skip + PAGE_SIZE)} data-testid="page-next">
                Siguiente →
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && closeDetail()}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="email-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <PaperPlaneTilt size={16} />
                  Detalle del envío
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Fecha</div>
                    <div className="font-mono text-xs mt-1">{selected.created_at?.replace("T", " ").slice(0, 19)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Estado</div>
                    <div className="mt-1"><StatusBadge item={selected} /></div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Tipo</div>
                    <div className="text-xs mt-1">{KIND_LABEL[selected.kind] || selected.kind || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Proveedor</div>
                    <div className="text-xs mt-1 font-mono uppercase">{selected.provider || "—"}</div>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Destinatario</div>
                  <div className="font-mono text-xs mt-1 flex items-center gap-2">
                    <span>{selected.to}</span>
                    {selected?.meta?.client_id && (
                      <button
                        onClick={() => openTimeline(selected.meta.client_id)}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 font-semibold border border-blue-200 dark:border-blue-900 rounded px-2 py-0.5"
                        data-testid="detail-timeline-btn"
                      >
                        <User size={10} weight="bold" /> Ver todos los correos de este cliente
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Asunto</div>
                  <div className="text-xs mt-1">{selected.subject}</div>
                </div>

                {/* Tracking events */}
                {(selected.delivered_at || selected.opened_at || selected.clicked_at || selected.bounced_at) && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1.5">Seguimiento</div>
                    <div className="flex flex-wrap gap-2 text-[11px]">
                      {selected.delivered_at && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200" title={new Date(selected.delivered_at).toLocaleString()}>
                          <CheckCircle size={11} weight="fill" /> Entregado · {new Date(selected.delivered_at).toLocaleString()}
                        </span>
                      )}
                      {selected.opened_at && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300" title={new Date(selected.opened_at).toLocaleString()}>
                          <Eye size={11} weight="fill" /> Abierto · {new Date(selected.opened_at).toLocaleString()}
                        </span>
                      )}
                      {selected.clicked_at && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300" title={new Date(selected.clicked_at).toLocaleString()}>
                          <CursorClick size={11} weight="fill" /> Clic · {new Date(selected.clicked_at).toLocaleString()}
                        </span>
                      )}
                      {selected.bounced_at && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300" title={new Date(selected.bounced_at).toLocaleString()}>
                          <XCircle size={11} weight="fill" /> Rebotó · {new Date(selected.bounced_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* HTML preview */}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1.5">Contenido del correo</div>
                  {detailLoading ? (
                    <div className="h-40 border border-slate-200 dark:border-slate-800 rounded flex items-center justify-center text-xs text-slate-400">Cargando…</div>
                  ) : detailDoc?.html ? (
                    <iframe
                      srcDoc={detailDoc.html}
                      sandbox=""
                      className="w-full h-[400px] border border-slate-200 dark:border-slate-800 rounded bg-white"
                      title="Preview del correo"
                      data-testid="email-html-preview"
                    />
                  ) : (
                    <div className="h-24 border border-dashed border-slate-200 dark:border-slate-800 rounded flex items-center justify-center text-[11px] text-slate-400 italic px-4 text-center">
                      Contenido HTML no disponible para este correo (envío previo a la mejora de auditoría).
                    </div>
                  )}
                </div>

                {selected.provider_id && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">ID del proveedor</div>
                    <div className="font-mono text-[11px] mt-1 break-all">{selected.provider_id}</div>
                  </div>
                )}
                {selected.error && (
                  <div className="rounded border border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/40 p-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-rose-700 dark:text-rose-300 font-semibold mb-1">Error</div>
                    <div className="text-xs text-rose-800 dark:text-rose-200 font-mono whitespace-pre-wrap break-all">{selected.error}</div>
                  </div>
                )}
                {selected.meta && Object.keys(selected.meta).length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Metadatos</div>
                    <pre className="text-[11px] mt-1 bg-slate-50 dark:bg-slate-950/40 border border-slate-200 dark:border-slate-800 rounded p-2 font-mono overflow-auto max-h-40">{JSON.stringify(selected.meta, null, 2)}</pre>
                  </div>
                )}
                {canResend(selected) && (
                  <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                    <Button
                      onClick={() => { resend(selected); closeDetail(); }}
                      disabled={resending === (selected.provider_id || selected.created_at)}
                      data-testid="detail-resend-btn"
                    >
                      {resending === (selected.provider_id || selected.created_at)
                        ? <ArrowsClockwise size={14} className="animate-spin mr-2" />
                        : <PaperPlaneTilt size={14} weight="fill" className="mr-2" />}
                      Reenviar correo
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Timeline per-client dialog */}
      <Dialog open={timelineOpen} onOpenChange={(o) => { if (!o) { setTimelineOpen(false); setTimelineData(null); }}}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="email-timeline-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ChatCircle size={16} />
              Historial de comunicaciones
              {timelineData?.client?.name && <span className="text-slate-500 font-normal">· {timelineData.client.name}</span>}
            </DialogTitle>
          </DialogHeader>
          {timelineLoading ? (
            <div className="py-12 text-center text-xs text-slate-500">Cargando historial…</div>
          ) : timelineData ? (
            <>
              {/* Summary strip */}
              <div className="grid grid-cols-5 gap-2 text-center bg-slate-50 dark:bg-slate-950/40 border border-slate-200 dark:border-slate-800 rounded p-3 mb-3">
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Total</div>
                  <div className="text-base font-mono font-semibold">{timelineData.total}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Entregados</div>
                  <div className="text-base font-mono font-semibold text-emerald-700 dark:text-emerald-400">{timelineData.sent}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Fallidos</div>
                  <div className="text-base font-mono font-semibold text-rose-700 dark:text-rose-400">{timelineData.failed}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Abiertos</div>
                  <div className="text-base font-mono font-semibold text-blue-700 dark:text-blue-400">{timelineData.opened}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Tasa apertura</div>
                  <div className="text-base font-mono font-semibold">{timelineData.open_rate}%</div>
                </div>
              </div>

              {timelineData.items.length === 0 ? (
                <EmptyState icon={EnvelopeSimple} title="Sin correos" subtitle="Este cliente aún no ha recibido correos." />
              ) : (
                <ul className="divide-y divide-slate-100 dark:divide-slate-800" data-testid="timeline-list">
                  {timelineData.items.map((it, i) => (
                    <li key={it.provider_id || `${it.created_at}-${i}`} className="py-2.5 flex items-start gap-3">
                      <div className="shrink-0 pt-0.5">
                        <StatusBadge item={it} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium text-slate-900 dark:text-white truncate">{it.subject || "(sin asunto)"}</div>
                        <div className="text-[10px] text-slate-500 font-mono mt-0.5">{fmtDate(it.created_at)} · {KIND_LABEL[it.kind] || it.kind || "—"}</div>
                        {(it.opened_at || it.clicked_at || it.bounced_at) && (
                          <div className="flex items-center gap-2 mt-1 text-[10px]">
                            {it.opened_at && <span className="inline-flex items-center gap-0.5 text-emerald-700 dark:text-emerald-400 font-semibold uppercase tracking-wider"><Eye size={10} weight="fill" /> abierto</span>}
                            {it.clicked_at && <span className="inline-flex items-center gap-0.5 text-blue-700 dark:text-blue-400 font-semibold uppercase tracking-wider"><CursorClick size={10} weight="fill" /> clic</span>}
                            {it.bounced_at && <span className="inline-flex items-center gap-0.5 text-rose-700 dark:text-rose-400 font-semibold uppercase tracking-wider"><XCircle size={10} weight="fill" /> rebote</span>}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
